import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Printer } from "@phosphor-icons/react";
import { humanizeAxiosError } from "@/lib/api";

const STATUS_LABEL = {
  pending: { label: "Beklemede", variant: "secondary" },
  approved: { label: "Onaylandı", variant: "default" },
  rejected: { label: "Reddedildi", variant: "destructive" },
};

export default function Promotions() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ target_user_id: "", to_position: "", to_salary: "", reason: "" });
  const [printData, setPrintData] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [petitionFile, setPetitionFile] = useState(null);

  const canRequest = ["manager", "admin", "hr"].includes(user?.role);
  const isHR = ["hr", "admin"].includes(user?.role);
  const isGM = ["gm", "admin"].includes(user?.role);

  const load = () => {
    api.get("/promotions").then((r) => setItems(r.data));
    api.get("/personnel").then((r) => setPersonnel(r.data));
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.target_user_id || !form.to_position || !form.reason) return toast.error("Zorunlu alanlar eksik");
    try {
      const fd = new FormData();
      fd.append("target_user_id", form.target_user_id);
      fd.append("to_position", form.to_position);
      fd.append("reason", form.reason);
      if (form.to_salary) fd.append("to_salary", String(form.to_salary));
      if (petitionFile) fd.append("file", petitionFile);
      await api.post("/promotions", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Terfi talebi oluşturuldu");
      setOpen(false);
      setForm({ target_user_id: "", to_position: "", to_salary: "", reason: "" });
      setPetitionFile(null);
      load();
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };

  const hrApprove = async (p) => {
    try { await api.post(`/promotions/${p.id}/hr-approve`, {}); toast.success("İK onayı verildi"); load(); }
    catch (e) { toast.error(humanizeAxiosError(e)); }
  };
  const gmApprove = async (p) => {
    try { await api.post(`/promotions/${p.id}/gm-approve`, {}); toast.success("GM onayı verildi ve terfi uygulandı"); load(); }
    catch (e) { toast.error(humanizeAxiosError(e)); }
  };
  const doReject = async () => {
    if (!rejectFor || !rejectReason) return;
    try { await api.post(`/promotions/${rejectFor.id}/reject`, { reason: rejectReason }); toast.success("Reddedildi"); setRejectFor(null); setRejectReason(""); load(); }
    catch (e) { toast.error(humanizeAxiosError(e)); }
  };

  const deptStaff = user?.role === "manager"
    ? personnel.filter((p) => p.department === user.department && p.id !== user.id)
    : personnel.filter((p) => p.id !== user?.id);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">İnsan Kaynakları</div>
          <h1 className="font-display text-3xl font-bold mt-1">Terfiler</h1>
        </div>
        {canRequest && (
          <Button onClick={() => setOpen(true)} data-testid="new-promo"><Plus size={16} className="mr-1" />Yeni Terfi Talebi</Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left p-3">Personel</th>
                  <th className="text-left p-3">Departman</th>
                  <th className="text-left p-3">Eski Görev</th>
                  <th className="text-left p-3">Yeni Görev</th>
                  <th className="text-left p-3">Talep Eden</th>
                  <th className="text-left p-3">İK</th>
                  <th className="text-left p-3">GM</th>
                  <th className="text-left p-3">Durum</th>
                  <th className="text-left p-3">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-t border-border">
                    <td className="p-3 font-medium">{p.target_name}</td>
                    <td className="p-3">{p.department}</td>
                    <td className="p-3">{p.from_position || "-"}</td>
                    <td className="p-3">{p.to_position}</td>
                    <td className="p-3">{p.requested_by_name}<div className="text-[11px] text-muted-foreground">{p.requested_at?.slice(0,10)}</div></td>
                    <td className="p-3"><Badge variant={p.hr_status === "approved" ? "default" : "secondary"}>{p.hr_status === "approved" ? "Onaylı" : "-"}</Badge></td>
                    <td className="p-3"><Badge variant={p.gm_status === "approved" ? "default" : "secondary"}>{p.gm_status === "approved" ? "Onaylı" : "-"}</Badge></td>
                    <td className="p-3"><Badge variant={STATUS_LABEL[p.final_status]?.variant || "outline"}>{STATUS_LABEL[p.final_status]?.label || p.final_status}</Badge></td>
                    <td className="p-3">
                      <div className="flex gap-1 flex-wrap">
                        {p.petition_url && (
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}${p.petition_url}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs underline text-primary"
                            data-testid={`promo-petition-${p.id}`}
                            title={p.petition_name || "Dilekçe"}
                          >
                            📄 Dilekçe
                          </a>
                        )}
                        {isHR && p.hr_status !== "approved" && p.final_status === "pending" && (
                          <Button size="sm" variant="outline" onClick={() => hrApprove(p)} data-testid={`hr-approve-${p.id}`}>İK Onayla</Button>
                        )}
                        {isGM && p.hr_status === "approved" && p.gm_status !== "approved" && p.final_status === "pending" && (
                          <Button size="sm" onClick={() => gmApprove(p)} data-testid={`gm-approve-${p.id}`}>GM Onayla</Button>
                        )}
                        {(isHR || isGM) && p.final_status === "pending" && (
                          <Button size="sm" variant="ghost" onClick={() => { setRejectFor(p); setRejectReason(""); }} data-testid={`reject-${p.id}`}>Reddet</Button>
                        )}
                        {(isHR || isGM || p.requested_by === user?.id) && (
                          <Button size="sm" variant="ghost" onClick={() => setPrintData(p)} data-testid={`print-${p.id}`}><Printer size={14} /></Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-muted-foreground">Terfi talebi yok</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Yeni Terfi Talebi</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Personel</Label>
              <Select value={form.target_user_id} onValueChange={(v) => setForm({...form, target_user_id: v})}>
                <SelectTrigger data-testid="promo-user"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>
                  {deptStaff.map((p) => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name} — {p.position}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Yeni Görev</Label><Input value={form.to_position} onChange={(e) => setForm({...form, to_position: e.target.value})} data-testid="promo-position" /></div>
              <div><Label>Yeni Maaş (opsiyonel)</Label><Input type="number" value={form.to_salary} onChange={(e) => setForm({...form, to_salary: e.target.value})} data-testid="promo-salary" /></div>
            </div>
            <div><Label>Gerekçe / Dilekçe</Label><Textarea rows={4} value={form.reason} onChange={(e) => setForm({...form, reason: e.target.value})} data-testid="promo-reason" /></div>
            <div>
              <Label>Dilekçe Eki (PDF / Word / JPG / PNG — opsiyonel)</Label>
              <Input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*"
                onChange={(e) => setPetitionFile(e.target.files?.[0] || null)}
                data-testid="promo-petition-file"
              />
              {petitionFile && (
                <div className="text-xs text-muted-foreground mt-1" data-testid="promo-petition-file-name">
                  Seçildi: <span className="font-medium">{petitionFile.name}</span> ({(petitionFile.size / 1024).toFixed(1)} KB)
                </div>
              )}
            </div>
            <div className="text-xs text-muted-foreground">Kayıtlı imzanız otomatik olarak dilekçeye eklenir.</div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button onClick={submit} data-testid="promo-submit">Gönder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Terfi Reddi</DialogTitle></DialogHeader>
          <Label>Ret gerekçesi</Label>
          <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="promo-reject-reason" />
          <Button variant="destructive" onClick={doReject} data-testid="promo-reject-submit" disabled={!rejectReason}>Reddet</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!printData} onOpenChange={(o) => !o && setPrintData(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Terfi Dilekçesi</DialogTitle></DialogHeader>
          {printData && (
            <div>
              <div className="printable bg-white text-black p-8 rounded border border-border">
                <div className="text-center mb-6">
                  <div className="font-display text-2xl font-bold">COLOSSAE HOTEL</div>
                  <div className="text-sm uppercase tracking-[0.3em]">Terfi Dilekçesi</div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><b>Personel:</b> {printData.target_name}</div>
                  <div><b>Departman:</b> {printData.department}</div>
                  <div><b>Eski Görev:</b> {printData.from_position}</div>
                  <div><b>Yeni Görev:</b> {printData.to_position}</div>
                  <div><b>Talep Tarihi:</b> {printData.requested_at?.slice(0,10)}</div>
                  <div><b>Talep Eden:</b> {printData.requested_by_name}</div>
                  {printData.from_salary != null && printData.to_salary != null && (
                    <>
                      <div><b>Eski Maaş:</b> {Number(printData.from_salary).toLocaleString("tr-TR")} ₺</div>
                      <div><b>Yeni Maaş:</b> {Number(printData.to_salary).toLocaleString("tr-TR")} ₺</div>
                    </>
                  )}
                  <div className="col-span-2"><b>Gerekçe:</b> {printData.reason}</div>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-8 border-t pt-4">
                  <div>
                    <div className="text-xs uppercase text-muted-foreground">Müdür İmzası</div>
                    <div className="text-xs mt-1">{printData.requested_by_name}</div>
                    {printData.manager_signature && <img src={printData.manager_signature} className="h-16" alt="mgr" />}
                    <div className="text-[10px] text-muted-foreground">{printData.requested_at?.slice(0,16).replace('T', ' ')}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase text-muted-foreground">İK İmzası</div>
                    <div className="text-xs mt-1">{printData.hr_by_name || "-"}</div>
                    {printData.hr_signature && <img src={printData.hr_signature} className="h-16" alt="hr" />}
                    <div className="text-[10px] text-muted-foreground">{printData.hr_signed_at?.slice(0,16).replace('T', ' ') || ""}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase text-muted-foreground">Genel Müdür İmzası</div>
                    <div className="text-xs mt-1">{printData.gm_by_name || "-"}</div>
                    {printData.gm_signature && <img src={printData.gm_signature} className="h-16" alt="gm" />}
                    <div className="text-[10px] text-muted-foreground">{printData.gm_signed_at?.slice(0,16).replace('T', ' ') || ""}</div>
                  </div>
                </div>
                {printData.final_status === "rejected" && printData.reject_reason && (
                  <div className="mt-4 p-3 border border-destructive/40 bg-destructive/10 rounded">
                    <div className="text-xs font-semibold text-destructive">Ret Gerekçesi</div>
                    <div className="text-sm">{printData.reject_reason}</div>
                  </div>
                )}
              </div>
              <div className="flex justify-end mt-4">
                <Button onClick={() => window.print()} data-testid="promo-print"><Printer size={16} className="mr-1" />Yazdır</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
